// A small dropdown menu: a trigger, a panel, and the three behaviours people expect from one —
// closes on Escape, closes when you click outside it, and returns focus to the trigger.
//
// Used by the workflow toolbar's overflow, so that a single row of navigation can hold the less
// frequent destinations without wrapping onto a second and third line.
import { useEffect, useId, useRef, useState, type ReactNode } from 'react';

export function Menu({
  label,
  trigger,
  children,
  align = 'right',
  /** Override the trigger's shape — a labelled button rather than the default square icon. */
  triggerClassName,
}: {
  /** Accessible name for the trigger. */
  label: string;
  trigger: ReactNode;
  children: (close: () => void) => ReactNode;
  align?: 'left' | 'right';
  triggerClassName?: string;
}) {
  const [open, setOpen] = useState(false);
  const wrap = useRef<HTMLDivElement>(null);
  const button = useRef<HTMLButtonElement>(null);
  const id = useId();

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        button.current?.focus();
      }
    };
    const onDown = (e: MouseEvent) => {
      if (!wrap.current?.contains(e.target as Node)) setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    window.addEventListener('mousedown', onDown);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('mousedown', onDown);
    };
  }, [open]);

  return (
    <div ref={wrap} className="relative">
      <button
        ref={button}
        type="button"
        aria-label={label}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? id : undefined}
        onClick={() => setOpen((v) => !v)}
        className={
          triggerClassName ??
          `tap grid h-9 w-9 place-items-center rounded-md border text-muted transition hover:text-ink ${
            open ? 'border-line bg-surface-2 text-ink' : 'border-transparent hover:bg-surface-2'
          }`
        }
      >
        {trigger}
      </button>
      {open && (
        <div
          id={id}
          role="menu"
          className="animate-rise absolute right-0 z-drawer mt-1.5 min-w-[13rem] overflow-hidden rounded-lg border border-line bg-surface p-1 shadow-level-3"
          style={align === 'left' ? { left: 0, right: 'auto' } : undefined}
        >
          {children(() => setOpen(false))}
        </div>
      )}
    </div>
  );
}

/** A label above a group of menu items. */
export function MenuLabel({ children }: { children: ReactNode }) {
  return (
    <div className="px-3 pb-1 pt-2 text-[11px] font-semibold uppercase tracking-[0.08em] text-muted">{children}</div>
  );
}
